import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
/* eslint-disable react/button-has-type */ // https://github.com/jsx-eslint/eslint-plugin-react/issues/1555
import { useTheme } from '../../theme';
import { cloneElement, useId } from 'react';
import { getBaseVariant } from './variants/base';
import { getGhostButton } from './variants/ghost';
import { getPrimaryButton } from './variants/primary';
import { getSecondaryButton } from './variants/secondary';
import { getOutlineButton } from './variants/outline';
import { getDestructiveButton } from './variants/destructive';
import { getLinkButton } from './variants/link';
import { getLinkInlineButton } from './variants/linkInline';
import { getSecondaryAltButton } from './variants/secondaryAlt';
import { getUpgradeButton } from './variants/upgrade';
import { getStatusButton } from './variants/status';
import { getOutlineDestructiveButton } from './variants/outlineDestructive';
import { BodyS } from '../../typography';
import { Status } from '../status';
import { useTouchClick } from '../../hooks';
import { getStylesBySize, getIconMarginBySize, getIconSize } from './button.utils';
import { STROKE_WIDTH, VIEWBOX_SIZE, CENTER, RADIUS, rotate, dash } from '../spinner/spinnerPrimitives';
import { useIntl } from '@doxyme/locale';
const variantOfType = (type)=>{
    const variants = {
        primary: getPrimaryButton,
        secondary: getSecondaryButton,
        ghost: getGhostButton,
        outline: getOutlineButton,
        destructive: getDestructiveButton,
        link: getLinkButton,
        linkInline: getLinkInlineButton,
        secondaryAlt: getSecondaryAltButton,
        upgrade: getUpgradeButton,
        outlineDestructive: getOutlineDestructiveButton
    };
    return variants[type];
};
const getBorderRadiusBySize = (size, theme)=>({
        regular: {
            borderRadius: theme.shape.radius8
        },
        small: {
            borderRadius: theme.shape.radius8
        },
        xsmall: {
            borderRadius: theme.shape.radius4
        }
    })[size];
const spinnerColorOfType = (type, theme)=>({
        primary: theme.animatedSpinner.brand.spinner,
        secondary: theme.animatedSpinner.contrast.spinner,
        secondaryAlt: theme.animatedSpinner.contrast.spinner,
        ghost: theme.animatedSpinner.contrast.spinner,
        outline: theme.animatedSpinner.contrast.spinner,
        outlineDestructive: theme.animatedSpinner.contrast.spinner,
        link: theme.animatedSpinner.link.spinner,
        linkInline: theme.animatedSpinner.link.spinner,
        destructive: theme.animatedSpinner.white.spinner,
        upgrade: theme.animatedSpinner.white.spinner
    })[type];
export const Button = ({ onClick, onMouseDown, children, type = 'primary', htmlType = 'button', width, size = 'regular', isLoading = false, isDisabled = false, startIcon, endIcon, className, innerRef, themeName, customIconSize, status, css, id, ...rest })=>{
    const theme = useTheme(themeName);
    const baseStyles = getBaseVariant(theme);
    const variant = variantOfType(type)(theme);
    const sizeVariants = getStylesBySize({
        size,
        type,
        theme,
        isLoading
    });
    const borderRadiusVariants = getBorderRadiusBySize(size, theme);
    const iconMargin = getIconMarginBySize(size);
    const autoId = useId();
    const buttonId = id || `button-${autoId}`;
    const { formatMessage } = useIntl();
    const handleClick = (event)=>{
        if (isDisabled) {
            return;
        }
        const target = event.currentTarget;
        target.blur();
        void onClick?.(event);
    };
    const { handleTouchStart, handleTouchMove, handleTouchEnd } = useTouchClick({
        onClick: onClick ? handleClick : undefined
    });
    const handleMouseDown = (event)=>{
        if (isDisabled) {
            return;
        }
        void onMouseDown?.(event);
    };
    if (typeof status !== 'undefined') {
        return /*#__PURE__*/ _jsxs("button", {
            ...rest,
            id: buttonId,
            type: htmlType,
            css: [
                getStatusButton({
                    theme,
                    type,
                    size
                }),
                width && {
                    width
                }
            ],
            className: className,
            ref: innerRef,
            onClick: handleClick,
            onMouseDown: handleMouseDown,
            onTouchStart: handleTouchStart,
            onTouchEnd: handleTouchEnd,
            onTouchMove: handleTouchMove,
            children: [
                /*#__PURE__*/ _jsx(Status, {
                    statusLevel: status,
                    size: size === 'xsmall' ? 20 : 24
                }),
                /*#__PURE__*/ _jsx(BodyS, {
                    fontWeight: "medium",
                    children: children
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx("button", {
        ...rest,
        id: buttonId,
        disabled: isDisabled || isLoading,
        onClick: handleClick,
        onMouseDown: handleMouseDown,
        onTouchStart: handleTouchStart,
        onTouchEnd: handleTouchEnd,
        onTouchMove: handleTouchMove,
        type: htmlType,
        className: className,
        ref: innerRef,
        css: {
            ...baseStyles,
            ...borderRadiusVariants,
            ...sizeVariants,
            ...variant.base,
            gap: iconMargin,
            pointerEvents: isLoading ? 'none' : 'auto',
            width,
            ...isDisabled && variant.disabled,
            ...isLoading && variant.isLoading,
            ...css
        },
        children: isLoading ? /*#__PURE__*/ _jsx("svg", {
            "aria-label": formatMessage({
                id: 'loading'
            }),
            width: size === 'xsmall' ? 16 : 20,
            height: size === 'xsmall' ? 16 : 20,
            viewBox: `0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`,
            role: "progressbar",
            css: {
                animation: `${rotate} 1.4s linear infinite`
            },
            children: /*#__PURE__*/ _jsx("circle", {
                cx: CENTER,
                cy: CENTER,
                r: RADIUS,
                fill: "none",
                stroke: spinnerColorOfType(type, theme),
                strokeWidth: STROKE_WIDTH,
                strokeLinecap: "round",
                css: {
                    animation: `${dash} 1.4s ease-in-out infinite`
                }
            })
        }) : /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                Boolean(startIcon) && size !== 'xsmall' && /*#__PURE__*/ cloneElement(startIcon, {
                    size: customIconSize ?? getIconSize(size)
                }),
                typeof children === 'string' ? /*#__PURE__*/ _jsx(BodyS, {
                    tag: "span",
                    fontWeight: "medium",
                    css: {
                        whiteSpace: 'nowrap'
                    },
                    children: children
                }) : children,
                Boolean(endIcon) && size !== 'xsmall' && /*#__PURE__*/ cloneElement(endIcon, {
                    size: customIconSize ?? getIconSize(size)
                })
            ]
        })
    });
};

//# sourceMappingURL=button.js.map